import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Award, Users, BookOpen, Globe } from "lucide-react";

const InstructorSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const achievements = [
    { icon: Award, value: "25+", label: "Années d'expérience" },
    { icon: Users, value: "1000+", label: "Thérapeutes formés" },
    { icon: BookOpen, value: "Auteur", label: "Plusieurs ouvrages" },
    { icon: Globe, value: "International", label: "Conférencier" },
  ];

  return (
    <section
      ref={ref}
      data-testid="instructor-section"
      className="relative py-24 lg:py-32 overflow-hidden"
      style={{ background: "#030014" }}
    >
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-purple-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-cyan-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Image Column */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="relative order-2 lg:order-1"
          >
            <div className="relative">
              {/* Glow effect behind image */}
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-cyan-500/20 rounded-2xl blur-2xl transform scale-105" />
              
              {/* Main Image */}
              <div className="relative rounded-2xl overflow-hidden border border-indigo-500/20">
                <img
                  src="https://gillesdelieuze.com/wp-content/uploads/2022/12/Gilles-20c-1024x683.jpg"
                  alt="Gilles Delieuze"
                  className="w-full h-auto object-cover"
                  style={{ filter: "saturate(0.9)" }}
                />
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#030014] via-transparent to-transparent opacity-60" />
              </div>

              {/* Floating Badge */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="absolute -bottom-6 -right-6 bg-gradient-to-r from-purple-600 to-cyan-600 p-1 rounded-xl"
              >
                <div className="bg-[#030014] px-6 py-4 rounded-lg">
                  <p className="text-2xl font-light text-gradient-cosmic" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    L'Agitateur
                  </p>
                  <p className="text-sm text-slate-400">de Particules</p>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Content Column */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="order-1 lg:order-2"
          >
            <span className="text-cyan-400 text-sm uppercase tracking-[0.2em] mb-4 block">
              Votre Guide
            </span>
            <h2
              className="text-4xl sm:text-5xl font-light text-white mb-6"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Qui est <span className="text-gradient-cosmic">Gilles Delieuze</span> ?
            </h2>

            <div className="space-y-6 text-slate-300 leading-relaxed">
              <p>
                Gilles Delieuze est un <strong className="text-white">expert reconnu en thérapies quantiques et énergétiques</strong>, 
                mais aussi mentor en développement personnel, leadership et spiritualité incarnée.
              </p>
              <p>
                Avec plus de <strong className="text-cyan-400">25 ans d'expérience</strong> dans le domaine, 
                il a accompagné des milliers de thérapeutes et de patients vers une expansion profonde et durable.
              </p>
              <p>
                Auteur de plusieurs ouvrages et conférencier international, Gilles est une{" "}
                <strong className="text-white">référence incontournable</strong> dans le monde de la thérapie holistique. 
                Son approche unique, combinant science et spiritualité, a transformé la vie de nombreux praticiens.
              </p>
              <p className="text-amber-400/90 italic">
                "Les enseignements qu'il dispense sont exclusifs et inédits. Ce qu'il enseigne ne se retrouve nulle part ailleurs."
              </p>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-10">
              {achievements.map((item, index) => {
                const Icon = item.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                    className="glass-card p-4 rounded-xl text-center"
                  >
                    <Icon className="w-6 h-6 text-purple-400 mx-auto mb-2" />
                    <div className="text-xl font-semibold text-white">{item.value}</div>
                    <div className="text-xs text-slate-500">{item.label}</div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default InstructorSection;
